import React from 'react';
import { Zap, Mail, ArrowRight, Trophy, Shield, Clock, Linkedin } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    Product: [
      { label: 'Features', href: '#features' },
      { label: 'How It Works', href: '#architecture' },
      { label: 'Methodology', href: '#methodology' },
      { label: 'Platforms', href: '#platforms' },
    ],
    Resources: [
      { label: 'See Demo', href: '#demo' },
      { label: 'FAQ', href: '#faq' },
      { label: 'Documentation', href: '#resources' },
      { label: 'Compliance', href: '#compliance' },
    ],
    Company: [
      { label: 'About', href: '#about' },
      { label: 'Contact', href: '#contact' },
      { label: 'Email Us', href: 'mailto:sales@astra-hft.com' },
    ],
  };

  const stats = [
    { icon: Zap, value: '~2ns', label: 'Amortized Per-Decision' },
    { icon: Trophy, value: '950M+', label: 'Decisions/sec (ARM64)' },
    { icon: Clock, value: '450M+', label: 'Decisions/sec (x86)' },
    { icon: Shield, value: "First Known", label: 'Codon-Native HFT' },
  ];

  return (
    <footer className="relative border-t border-border overflow-hidden">
      {/* Background effect */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl" />
      </div>

      {/* Pre-footer CTA */}
      <div className="py-12 border-b border-border relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              <span className="text-shimmer">First Known.</span> A New Category in HFT.
            </h3>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              First known pure-software 950M+ decisions/sec engine. Achieved on commodity laptops — designed for continuous hot-path execution in production.
            </p>
            <a
              href="#contact"
              className="group inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold rounded-xl hover:opacity-90 transition-all"
            >
              Get In Touch
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </div>

      {/* Stats bar */}
      <div className="py-8 bg-card/30 border-b border-border relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <div key={index} className="flex items-center justify-center gap-3">
                <stat.icon className="w-5 h-5 text-cyan-400" />
                <div>
                  <span className="text-lg font-bold text-white">{stat.value}</span>
                  <span className="text-xs text-muted-foreground ml-1">{stat.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main footer */}
      <div className="py-16 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-12 mb-12">
            {/* Brand */}
            <div className="lg:col-span-2">
              <a href="#" className="flex items-center gap-3 mb-6 group">
                <div className="relative w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center group-hover:shadow-[0_0_20px_rgba(34,211,238,0.3)] transition-shadow">
                  <Zap className="w-7 h-7 text-white" />
                  <div className="absolute inset-0 bg-white/20 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <div className="flex flex-col">
                  <span className="text-xl font-bold text-white leading-tight tracking-tight">ASTRA</span>
                  <span className="text-xs font-semibold text-cyan-400 leading-tight tracking-wider">EXPRESS LANE</span>
                </div>
              </a>
              <p className="text-sm text-muted-foreground mb-6 max-w-xs leading-relaxed">
                First known Codon-native HFT engine. 950M+ decisions/sec on commodity laptops. Designed for continuous hot-path execution in co-located production.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <a
                  href="mailto:sales@astra-hft.com"
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-card border border-border hover:border-cyan-500/30 text-muted-foreground hover:text-white transition-all"
                >
                  <Mail className="w-4 h-4" />
                  <span className="text-sm">sales@astra-hft.com</span>
                </a>
                {/* LinkedIn Company Page */}
                <a
                  href="https://www.linkedin.com/company/110255340"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Follow on LinkedIn"
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#0A66C2]/10 border border-[#0A66C2]/30 hover:bg-[#0A66C2]/20 text-[#0A66C2] transition-all"
                >
                  <Linkedin className="w-4 h-4" />
                  <span className="text-sm font-medium">LinkedIn</span>
                </a>
                {/* F6S Follow Button */}
                <a
                  href="https://www.f6s.com/member/santosh-t?follow=1"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Follow on F6S"
                  className="flex items-center"
                >
                  <img
                    src="https://www.f6s.com/images/f6s-follow-secondary.png"
                    alt="Follow on F6S"
                    className="w-[78px] h-[22px] hover:opacity-80 transition-opacity"
                  />
                </a>
              </div>
            </div>

            {/* Links */}
            {Object.entries(footerLinks).map(([title, links]) => (
              <div key={title}>
                <h4 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">{title}</h4>
                <ul className="space-y-3">
                  {links.map((link) => (
                    <li key={link.label}>
                      <a
                        href={link.href}
                        className="text-sm text-muted-foreground hover:text-cyan-400 transition-colors inline-flex items-center gap-1 group"
                      >
                        {link.label}
                        <ArrowRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Legal Disclaimer */}
          <div className="py-6 border-t border-border mb-6">
            <p className="text-xs text-muted-foreground leading-relaxed max-w-4xl mx-auto text-center">
              <span className="text-white font-medium">Important:</span> ASTRA Express Lane is a trading infrastructure platform.
              We do NOT provide trading strategies, investment advice, or trading signals. All sample indicators
              (RSI, MACD, etc.) included in demos are for benchmarking purposes only and do not constitute investment advice.
              You are solely responsible for developing, testing, and deploying your own trading logic.
              Trading involves substantial risk of loss. Past performance of any system or methodology is not
              necessarily indicative of future results.
            </p>
          </div>

          {/* Bottom */}
          <div className="pt-8 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              &copy; {currentYear} Astra Fintech Labs. All rights reserved.
            </p>
            <div className="flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
              </span>
              <span className="text-xs text-muted-foreground">
                Built for <span className="text-green-400 font-medium">maximum performance</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
